/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

/**
 * Copyright (c) 2013 MOCA Systems, Inc.  All rights reserved.
 *
 * Angular controllers for app.js.
 */

'use strict';

import * as angular from 'angular';
import * as moment from 'moment';
import { soak, checkNested } from 'utils/basic-utils';
import { dateJar } from 'utils/datetime-utils';
import { randomColor } from 'utils/color-utils';
import * as strings from '@touchplan/strings';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import * as _ from 'lodash-es';

import { combineLatest, firstValueFrom, forkJoin, of, Subject } from 'rxjs';
import { last, filter, map, takeUntil, delay, take } from 'rxjs/operators';

import Hud from '@appLegacy/lib/ui/Hud';
import Point from '@appLegacy/lib/math/Point';

import TicketFilter from '@appLegacy/model/Ticket';

import Alignment from '@appLegacy/lib/ui/Alignment';
import TicketOps from '@appLegacy/lib/ui/TicketOperations';
import Timeline from '@appLegacy/lib/ui/Timeline';

import { LegacyRouting } from 'src/app/services/legacy-helpers/legacy-routing.service';

import * as sillyStuff from '@appLegacy/lib/silly-stuff';
import { PlanState, StateEvents } from 'src/app/services/plan-state.service';

import { ProjectNotifier } from 'src/app/services/project-notifier.service';

import { ValidatedPlanActions } from 'src/app/services/validated-plan-actions.service';
import { fbdbTime } from 'src/app/plain-javascript/background-scripts/fbdb-time';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { OverviewProjectService } from 'src/app/services/overview-project.service';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { pokeProjectPresence } from '@touchplan/firebase-common';
import { fancyFirebaseReferencer, fancyFirebaseGetters } from '@touchplan/fancy-referencer';
import { ILoggerService } from '@touchplan/logging';

import { Directive, ElementRef, Injector, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UpgradeComponent } from '@angular/upgrade/static';
import { LevelOfDetail, LEVEL_OF_DETAIL_TABLE_STRINGS } from '@appPlanApplication/lod/LevelOfDetail';
import { SWIMLANE_CANNOT_CREATE_TO_FAR } from '@touchplan/strings';
import { ProjectDialogLauncher } from 'src/app/common/project-dialogs/project-dialog-launcher.service';
import { MatDialog } from '@angular/material/dialog';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ProcoreRFIStatus, ProcoreService } from 'src/app/services/procore.service';
import { AppState } from 'src/app/services/app-state.service';
import { UserPreferenceStateService } from 'src/app/services/user-preference-state.service';
import { ReportsSwimlaneDataService } from 'src/app/modules/reports/services/reports-swimlane-data.service';

import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import { hasRestrictedAccess } from 'src/app/services/plan-access-check-utils';
import { PlanAccessLevel } from '@touchplan/common';
import { PlanPrintPanelRemoteControlService } from 'src/app/services/plan-print-panel-remote-control.service';
import { PlanTicketsFilterRemoteControlService } from 'src/app/services/plan-tickets-filter-remote-control.service';
import { PlanPrintService } from 'src/app/modules/plan/services/plan-print.service';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { fancyDatabase } from '@touchplan/client-firebase';

import firebase from 'firebase/compat/app';

declare const window: any;

export class PlanController {
	public static $inject = [
		'$filter',
		'$http',
		'$q',
		'$rootScope',
		'$sce',
		'$scope',
		'$timeout',
		'DatePositionValidator',
		'OrderedExecutor',
		'ProjectViewApi',
		'SystemColors',
		'activeColumnService',
		'appState',
		'developerMode',
		'fbConnection',
		'fbdbRequests',
		'firebaseConstraints2',
		'firebaseCounter',
		'firebaseIndex',
		'firebaseIndices',
		'firebaseInvitedUsers',
		'firebaseLocations',
		'firebaseMeshedUsersGlobal',
		'firebasePriorities',
		'firebaseProjectColors',
		'firebaseProjectMembers',
		'firebaseProjectWorkdays',
		'firebasePromisePeriod3',
		'firebasePullColumns',
		'firebaseRefUrl',
		'firebaseRoles',
		'firebaseSnapshots',
		'firebaseTickets',
		'firebaseTimelineTickets',
		'firebaseUsers',
		'firebaseVariances',
		'floatingCalendar',
		'focusManager',
		'intercomHandler',
		'multipleRolesUtils',
		'popup',
		'projectNotifier',
		'validatedPlanActions',
		'rallyPointService',
		'settingStorage',
		'shiftApi',
		'subscriber',
		'ticketEditService',
		'watcherStore',
		'legacyRouting',
		'featureFlagManager',
		'overviewProjectService',
		'planTicketsFilterRemoteControlService',
		'planPrintPanelRemoteControlService',
		'planPrintService',
		'matDialog',
		'projectDialogLauncher',
		'procoreService',
		'userPreferenceStateService',
		'reportsSwimlaneDataService',
		'planAccessCheck',
		'bannerMessageHandler',
	];
	public planState: PlanState;
	public legacyState;

	public strings = strings;

	constructor(
		public $filter,
		public $http,
		public $q,
		public $rootScope,
		public $sce,
		public $scope,
		public $timeout,
		public DatePositionValidator,
		public OrderedExecutor,
		public ProjectViewApi,
		public SystemColors,
		public activeColumnService,
		public appState: AppState,
		public developerMode,
		public fbConnection,
		public fbdbRequests,
		public firebaseConstraints2,
		public firebaseCounter,
		public firebaseIndex,
		public firebaseIndices,
		public firebaseInvitedUsers,
		public firebaseLocations,
		public firebaseMeshedUsersGlobal,
		public firebasePriorities,
		public firebaseProjectColors,
		public firebaseProjectMembers,
		public firebaseProjectWorkdays,
		public firebasePromisePeriod3,
		public firebasePullColumns,
		public firebaseRefUrl,
		public firebaseRoles,
		public firebaseSnapshots,
		public firebaseTickets,
		public firebaseTimelineTickets,
		public firebaseUsers,
		public firebaseVariances,
		public floatingCalendar,
		public focusManager,
		public intercomHandler,
		public multipleRolesUtils,
		public popup,
		public projectNotifier: ProjectNotifier,
		public validatedPlanActions: ValidatedPlanActions,
		public rallyPointService,
		public settingStorage,
		public shiftApi,
		public subscriber,
		public ticketEditService,
		public watcherStore,
		public legacyRouting: LegacyRouting,
		public featureFlagManager: FeatureFlagManager,
		public overviewProjectService: OverviewProjectService,
		public planTicketsFilterRemoteControlService: PlanTicketsFilterRemoteControlService,
		public planPrintPanelRemoteControlService: PlanPrintPanelRemoteControlService,
		public planPrintService: PlanPrintService,
		public matDialog: MatDialog,
		public projectDialogLauncher: ProjectDialogLauncher,
		private procoreService: ProcoreService,
		private userPreferenceStateService: UserPreferenceStateService,
		public reportsSwimlaneDataService: ReportsSwimlaneDataService,
		public planAccessCheck: PlanAccessCheck,
		public bannerMessageHandler: BannerMessageHandler
	) {
		this.logger = LoggerServiceFactory.create('PlanController');
	}

	public shutdownSubject = new Subject<void>();
	private logger: ILoggerService;

	public doStuff(
		$filter,
		$http,
		$q,
		$rootScope,
		$sce,
		$scope,
		$timeout,
		DatePositionValidator,
		OrderedExecutor,
		ProjectViewApi,
		SystemColors,
		activeColumnService,
		appState: AppState,
		developerMode,
		fbConnection,
		fbdbRequests,
		firebaseConstraints2,
		firebaseCounter,
		firebaseIndex,
		firebaseIndices,
		firebaseInvitedUsers,
		firebaseLocations,
		firebaseMeshedUsersGlobal,
		firebasePriorities,
		firebaseProjectColors,
		firebaseProjectMembers,
		firebaseProjectWorkdays,
		firebasePromisePeriod3,
		firebasePullColumns,
		firebaseRefUrl,
		firebaseRoles,
		firebaseSnapshots,
		firebaseTickets,
		firebaseTimelineTickets,
		firebaseUsers,
		firebaseVariances,
		floatingCalendar,
		focusManager,
		intercomHandler,
		multipleRolesUtils,
		popup,
		projectNotifier: ProjectNotifier,
		validatedPlanActions: ValidatedPlanActions,
		rallyPointService,
		settingStorage,
		shiftApi,
		subscriber,
		ticketEditService,
		watcherStore,
		legacyRouting: LegacyRouting,
		featureFlagManager: FeatureFlagManager,
		overviewProjectService: OverviewProjectService,
		planTicketsFilterRemoteControlService: PlanTicketsFilterRemoteControlService,
		planPrintPanelRemoteControlService: PlanPrintPanelRemoteControlService,
		planPrintService: PlanPrintService,
		matDialog: MatDialog,
		projectDialogLauncher: ProjectDialogLauncher,
		logger: ILoggerService,
		procoreService: ProcoreService,
		userPreferenceStateService: UserPreferenceStateService,
		reportsSwimlaneDataService: ReportsSwimlaneDataService,
		planAccessCheck: PlanAccessCheck,
		bannerMessageHandler: BannerMessageHandler
	) {
		$scope.tester = function (e) {
			logger.log(e);
		};

		$scope.legacyImages = {
			navBarIcon: 'assets/images/touchplan-nav-bar-icon.png',
			milestoneIcon: 'assets/images/milestone-icon.png',
			roleIcon: 'assets/images/roles-icon-white.svg',
			projectViewIcon: 'assets/images/project-view-icon.svg',
			addTaskIcon: 'assets/images/add-ticket-icon.svg',
			addConstraintIcon: 'assets/images/add-constraint-icon.svg',
			addMilestoneIcon: 'assets/images/add-milestone--icon.svg',
		};

		// this.legacyState = this.legacyRouting.oldStyleSnapshot();
		this.legacyState = {
			projectId: this.planState.projectId,
			planId: this.planState.planId,
		};
		$scope.legacyState = this.legacyState;
		//don't really feel like fixing "this" at the moment
		var legacyState = this.legacyState;
		logger.log('legacyState', legacyState);

		$scope.userPreferenceStateService = userPreferenceStateService;
		$scope.disableWebGL = userPreferenceStateService.disableWebGL;
		$scope.enableTicketOverlap = userPreferenceStateService.enableTicketOverlap;
		$scope.hideCompletedTickets = userPreferenceStateService.hideCompletedTickets;
		$scope.enableSolidLines = userPreferenceStateService.enableSolidLines;
		$scope.enableDependencyText = userPreferenceStateService.enableDependencyText;
		$scope.autoOpenProjectOverview = userPreferenceStateService.autoOpenProjectOverview;
		$scope.canvasTextOptions = userPreferenceStateService.canvasTextOptions;
		$scope.useModernTimeline = userPreferenceStateService.useModernTimeline;

		$scope.planAccessLevel = PlanAccessLevel;

		$scope.sessionStorageForLegacy = {};
		userPreferenceStateService.preferences$.pipe(takeUntil(this.shutdownSubject)).subscribe(() => {
			$scope.userPreferenceStateService = userPreferenceStateService;
			$scope.disableWebGL = userPreferenceStateService.disableWebGL;
			$scope.enableTicketOverlap = userPreferenceStateService.enableTicketOverlap;
			$scope.hideCompletedTickets = userPreferenceStateService.hideCompletedTickets;
			$scope.enableSolidLines = userPreferenceStateService.enableSolidLines;
			$scope.enableDependencyText = userPreferenceStateService.enableDependencyText;
			$scope.autoOpenProjectOverview = userPreferenceStateService.autoOpenProjectOverview;
			$scope.canvasTextOptions = userPreferenceStateService.canvasTextOptions;
			$scope.useModernTimeline = userPreferenceStateService.useModernTimeline;
		});
		dumpServicesOntoScope();

		$scope.datePositionValidator.forceState($scope.legacyState.projectId, $scope.legacyState.planId);

		$scope.orderedExecution = new OrderedExecutor();

		$scope.flags = {
			stickyInEditMode: false,
		};

		$scope.hud = makeHud();

		$scope.dependencyNotice = function () {
			this.planState.dependencies.toggleAll();
		};
		$scope.strings = strings;

		$scope.rallyMembers = { oldList: [], newList: [] };

		/*
		Edit Area Api:
			toggle = toggles edit state
			save = pushes the edit variable value to the model
			cancel = resets the edit value to be the model value and exits edit mode
			set(value) = sets the value of the model to 'value'
			get = returns the current model value
			getEdit = returns the current edit variable value
		 */
		$scope.editAreaApi = {};
		$scope.constraintSidePanelApi = {};

		$scope.projectSettings = {
			keys: ['useIntersectingPromisePeriods'],
			planState: this.planState,
			update: function () {
				//logger.log('update');
				this.keys.forEach(function (key) {
					if ($scope.project[key] !== undefined) {
						this[key] = $scope.project[key];
					} else {
						this[key] = null;
					}
					//this.analyticsStuff(key);
				}, this);
			},
			updateKey: function (key) {
				//legacy patching - use semi-modern writes, coorce undefined as a valid null
				this.planState.project.listRef.query.child(key).set(this[key] || null);
			},
		};

		$scope._allLoaded = false;

		var firebaseList = [];
		/** string based list of things to null out on destroy, is sort of a
		band-aid memory leak thing since this scope is so pervasive */
		var nullList = [
			'roleColors',
			'locationColors',
			'pp',
			'tickets',
			'roles',
			'locations',
			'$projectWorkdays',
			'priorities',
			'projectMember',
			'floatingTickets',
			'snapshots',
			'projectMembers',
			'statusOptions',
			'plans',
			'growActiveSpace',
			'growPullSpace',
			'plan',
			'activeLine',
			'activeLineDate',
			'constraints2',
			'timelineTickets',
			'pullColumns',
			'milestone',
			'today',
			'milestoneX',
			'scheduleX',
			'milestoneName',
			'project',
			'space',
		];
		var unwatchList = [];

		$scope.$on('$destroy', (obj) => {
			logger.log('destroy');
			// MOC-3065
			if ($scope.procoreCanceller) {
				$scope.procoreCanceller.resolve();
			}
			this.shutdownSubject.next();

			// MOC-3056
			if ($scope.procoreTimeout) {
				clearTimeout($scope.procoreTimeout);
			}
			$scope.shiftApi.empty();
			$scope.print.toggle(false);
			// MOC-2893...Doing a null check to supress the error for R33. Should probably figure out where error is coming from...
			if ($scope.projectViewApi && $scope.projectViewApi.destroy) {
				$scope.projectViewApi.destroy();
			}
			for (var i = firebaseList.length - 1; i >= 0; i--) {
				firebaseList[i].$destroy();
			}
			unwatchList.forEach((fn) => fn());
			$scope.orderedExecution.destroy();
			firebaseList = [];
			activeColumnService.reset();
			window.MCScope = null;
			if ($scope.hud) {
				$scope.hud.destroy();
			}
			watcherStore.reset();

			$scope.hud = null;
			nullList.forEach((key) => {
				$scope[key] = null;
			});
			$scope.planState = null;

			window.screenSpace = null;

			// MOC-3976
			if ($scope.activeLineCalendarShutdown) {
				$scope.activeLineCalendarShutdown();
			}

			if ($scope.milestoneLineCalendarShutdown) {
				$scope.milestoneLineCalendarShutdown();
			}
		});
		const projectPath = fancyFirebaseGetters.getProjectsPath(legacyState.projectId);
		$scope.roleColors = new firebaseProjectColors(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectPath, legacyState.projectId)
				.child('colors')
				.child('roles')
		);
		$scope.locationColors = new firebaseProjectColors(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectPath, legacyState.projectId)
				.child('colors')
				.child('locations')
		);
		$scope.roleColors.$loaded().then(function () {
			$scope.roleColors.forceConvertUsedColorsToHexString();
		});
		$scope.locationColors.$loaded().then(function () {
			$scope.locationColors.forceConvertUsedColorsToHexString();
		});

		firebaseList.push($scope.roleColors, $scope.locationColors);

		$scope.scrollWorkDayOnNewExceptionDay = function () {
			// The ipad is space limited, so scroll the whole panel down
			// to 40 pixels from the top if it's less than that.  This
			// ensure the buttons show on a new exception day.
			$('.panel.sidebar-panel.location-panel').animate({ scrollTop: 40 });
		};

		// 1389 878
		$scope.print = {
			showDialog: false,
			dpi: 96,
			pixelRatio: (<any>window).devicePixelRatio || 1,
			margin: 0.19685,
			width: 11,
			height: 8.5,
			textScale: 1,
			// Default paper sizes pulled from https://www.archtoolbox.com/representation/graphic-symbols/paper-sizes.html
			paperSizes: [
				{ id: 1, width: 8.5, height: 11, label: 'ANSI A' },
				{ id: 2, width: 11, height: 17, label: 'ANSI B' },
				{ id: 3, width: 17, height: 22, label: 'ANSI C' },
				{ id: 4, width: 22, height: 34, label: 'ANSI D' },
				//{id: 5, width: 34, height: 34, label: "ANSI E"},
				{ id: 5, width: 9, height: 12, label: 'ARCH A' },
				{ id: 6, width: 12, height: 18, label: 'ARCH B' },
				{ id: 7, width: 18, height: 24, label: 'ARCH C' },
				{ id: 8, width: 24, height: 36, label: 'ARCH D' },
				//{id: 10, width: 36, height: 48, label: "ARCH E"},
				{ id: 9, width: 8.5, height: 14, label: 'Legal' },
				{ id: 10, width: 23.4, height: 33.1, label: 'A1' },
				{ id: 11, width: 16.5, height: 23.4, label: 'A2' },
				{ id: 12, width: 11.7, height: 16.5, label: 'A3' },
				{ id: 13, width: 8.3, height: 11.7, label: 'A4' },
			],
			selectedPaperSize: null,
			isLandscape: false,
			//todo, scaling tends to be very broken when trying to scale larger
			scale: function (pageWidth, pageHeight, margin) {
				var dpi = 96;
				margin = margin || 0.19685; //hard code of 0.5cm
				pageWidth = pageWidth || 11;
				pageHeight = pageHeight || 8.5;
				var target = new Point((pageWidth - margin * 2) * dpi, (pageHeight - margin * 2) * dpi);
				var base = new Point(window.innerWidth, window.innerHeight);
				var dim = 'x';

				if (base[dim] > target[dim]) {
					var scale = target[dim] / base[dim];
				} else {
					//duh, trying to scale things based on a "fit to screen" system makes absolutely no
					//sense when the target is larger then the screen, so any adjustment is just going
					//to feel wrong. So...simply don't scale it. The right and bottom won't perfectly line
					//up, but you can't see it anyways to compare, so...
					//var scale = 1;
					var scale = target[dim] / base[dim];
				}
				return $scope.hud.scaleString(scale, new Point(0, 77));
				/*
				var ele = $('<div class="print-region">');
				ele.css({
					position:"absolute",
					top: 0,
					left: 0,
					width: target.x,
					height: target.y,
					"z-index": "1000000",
					background: "rgba(255, 0, 0, 0.4)",
					overflow: "hidden"
				});
				var body = $('body');
				body.append(ele);
				function handler(){
					body.off('click', handler);
					$scope.hud.setMatrix(lastMatrix);
					ele.remove();
				}
				var h = body.on('click', handler);

				var lastMatrix = $scope.hud.lock();
				logger.log('internal', $scope.hud.scaleString(scale, new Point(0,77)));

				$scope.hud.startAnimation();
				$scope.hud.scale(scale, new Point(0,77));
				$scope.hud.endAnimation();
				*/
			},
			paperSizeChanged: function () {
				this.width = this.selectedPaperSize.width;
				this.height = this.selectedPaperSize.height;
				this.isLandscape = false;
			},
			lastElement: null,
			lastMatrix: null,
			clear: function () {
				if (this.lastElement) {
					this.lastElement.remove();
					this.lastElement = null;
					this.lastSizingElement.remove();
					$scope.hud.setMatrix(this.lastMatrix);
				}
			},
			toggle: function (force?: boolean) {
				var open;
				if (force !== undefined) {
					open = !force;
				} else {
					open = this.showDialog;
				}
				// MOC-2949
				if (open) {
					// Turn off forced LOD for canvas
					LevelOfDetail.clearForcedLOD();
					// Resize renderer back to regular size.
					if (window.TouchPlanCanvas) {
						const newW = $scope.hud.w;
						const newH = $scope.hud.h;
						window.TouchPlanCanvas.printResize(newW, newH);
					}
					this.clear();
					activeColumnService.overrideCull();
					activeColumnService.overrideScreenRender(false);
					this.showDialog = false;
					$scope.roleSelector.enable = false;
				} else {
					this.selectedPaperSize = this.paperSizes[0];
					this.paperSizeChanged();
					this.showDialog = true;
					this.resize(this.width, this.height);
					$scope.roleSelector.enable = true;
				}
			},
			resize: function (pageWidth, pageHeight, margin) {
				var dpi = 96;
				margin = margin || 0.19685; //hard code of 0.5cm
				pageWidth = pageWidth || 11;
				pageHeight = pageHeight || 8.5;

				var target = new Point((pageWidth - margin * 2) * dpi, (pageHeight - margin * 2) * dpi);
				var base = new Point(window.innerWidth, window.innerHeight);
				var dim = 'x';

				var scale = target[dim] / base[dim];

				if (base[dim] > target[dim]) {
					var columnRenderScale = scale;
				} else {
					//duh, trying to scale things based on a "fit to screen" system makes absolutely no
					//sense when the target is larger then the screen, so any adjustment is just going
					//to feel wrong. So...simply don't scale it. The right and bottom won't perfectly line
					//up, but you can't see it anyways to compare, so...
					var columnRenderScale = 1; //base[dim]/target[dim];
				}
				//return $scope.hud.scaleString(scale, new Point(0,77));
				//logger.log('target', target, 'base', base);
				this.clear();

				//so...the magic here seems to be that we need a div sized to an explicit pixel value on body to tell print what we want printed
				// and we need to a matrix transform to fit that explicit div so that everything still looks fine when everything is printing
				var sizingElement = $('<div class="print-region-sizer"></div>');
				sizingElement.css({
					width: target.x,
					height: target.y,
				});

				var ele = $('<div class="print-region"><div class="right"></div><div class="bottom"></div></div>');
				ele.find('.bottom').css({
					height: base.y - target.y > 0 ? base.y - target.y : 0,
				});
				ele.find('.right').css({
					height: target.y,
					width: Math.abs(base.x - target.x),
					left: target.x,
				});
				var body = $('body');
				body.append(sizingElement);
				body.append(ele);
				var self = this;
				function handler() {
					$timeout(function () {
						// Resize renderer back to regular size.
						if (window.TouchPlanCanvas) {
							const newW = $scope.hud.w;
							const newH = $scope.hud.h;
							window.TouchPlanCanvas.printResize(newW, newH);
						}
						LevelOfDetail.clearForcedLOD();
						self.clear();
						activeColumnService.overrideCull();
						activeColumnService.overrideScreenRender(false);
						self.showDialog = false;
						$scope.roleSelector.enable = false;
					});
				}
				$timeout(function () {
					var h = ele.on('click', handler);
				});

				this.lastMatrix = $scope.hud.lock();
				this.lastElement = ele;
				this.lastSizingElement = sizingElement;

				var matrixString = $scope.hud.scaleString(scale, new Point(0, 77));

				var oldScale = $scope.hud.z;

				// var css = '@media print{ .zoomContent{ transform: '+matrixString+'!important; } }';
				// logger.log('css', css);
				// dynamicStyleSheet.create();
				// dynamicStyleSheet.addRule(css);
				const p = new Point(0, 77);
				$scope.hud.startAnimation();
				$scope.hud.scale(scale, p);
				$scope.hud.endAnimation();

				LevelOfDetail.forceLOD(LEVEL_OF_DETAIL_TABLE_STRINGS.ALWAYS);
				if (window.TouchPlanCanvas) {
					const targetMatrix: any = $scope.hud.lock();
					const scaledW: number = $scope.print.width * this.dpi;
					const scaledH: number = $scope.print.height * this.dpi;
					const newW: number =
						this.pixelRatio > 1 ? $scope.hud.w * 2 : scaledW < $scope.hud.w ? $scope.hud.w : scaledW;
					const newH: number =
						this.pixelRatio > 1 ? $scope.hud.h * 2 : scaledH < $scope.hud.h ? $scope.hud.h : scaledH;
					window.TouchPlanCanvas.printResize(newW, newH);

					// Tween the camera
					window.TouchPlanCanvas.camera.focusOnXYZ(targetMatrix.tx, targetMatrix.ty, targetMatrix.d);
				}
				//
				//logger.log('columnRenderScale', columnRenderScale);
				activeColumnService.overrideCull(columnRenderScale);
				activeColumnService.overrideScreenRender(true);
				$scope.lod.forceMax();
			},
			batch: function () {
				var supportedSizeList = [
					{ x: 11, y: 8.5 },
					{ x: 8.5, y: 11 },
				];

				supportedSizeList.forEach(function (spec) {
					logger.log(this.scale(spec.x, spec.y));
				}, this);
			},
			landscapeChanged: function () {
				const width: number = this.width;
				this.width = this.height;
				this.height = width;
			},
		};
		//$scope.print.screwingAround();

		// This isn't being used anywhere? Comment it out for now.
		//$scope.thingAtPlace = thingAtPlace;
		$scope.isLoadingSnapshot = false;

		$scope.iconStatus = {};

		if (!legacyState.projectId || !legacyState.planId) {
			abortLoading();
			return;
		}

		const planPath = fancyFirebaseGetters.getPlanPath(legacyState.projectId, legacyState.planId);
		$scope.pp = new firebasePromisePeriod3(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
		);

		$scope.selectInPeriod = function (id, exclusive, intersect) {
			if (intersect === undefined) {
				intersect = true;
			}
			if (exclusive) {
				$scope.tickets.selection.empty();
			}
			var period = $scope.pp[id];
			//$scope.tickets.selection.empty()
			logger.log('period', period.startX, period.endX);
			$scope.tickets.selectTicketsInRange(
				period.startX,
				period.endX,
				function (t) {
					return t.data.type !== 'constraint';
				},
				intersect
			);
		};

		$scope.lockCurrentPeriod = function () {
			if ($scope.promiseLockInProgress) {
				return;
			}
			var nextId = null;
			//if(!$scope.pp.canLock){ Logging.warning("Cannot lock the period"); return;}
			var shouldIntersect = !!$scope.project.useIntersectingPromisePeriods;
			$scope.selectInPeriod('promisePeriodNext', true, shouldIntersect);

			return popup({ description: "You're about to Promise the current period" })
				.then(function () {
					$scope.promiseLockInProgress = true;
					var extra = { initiatedBy: 'systemOnPromisePeriodLock' };
					return $scope.snapshots.takeSnapshot(
						legacyState.projectId,
						legacyState.planId,
						appState.userSync.$id,
						extra
					);
				})
				.then(function () {
					return $scope.pp
						.lock(function () {
							$scope.selectInPeriod('promisePeriodNext', true, shouldIntersect);
							return $scope.addPins($scope.tickets.selection.selectedItems, { pinType: 'period' });
						})
						.then(function (nextPeriodId) {
							nextId = nextPeriodId;
						});
				})
				.then(function () {
					analyticsEventsService.promisePeriodLocked(
						legacyState.planId,
						$scope.plan.name,
						nextId,
						$scope.tickets.selection.count()
					);
				})
				.finally(function () {
					// due to timing in rare cases, the tickets could be cleared when this finally is called. See TP-431
					if ($scope.tickets) {
						$scope.tickets.selection.empty();
					}
					$scope.promiseLockInProgress = false;
				});

			//maybe clear the selection? Undecided
		};

		//rule seems to be "can only remove black pin"
		//so... no actualFinish and no varianceId
		$scope.cantRemovePin = function (ticket, bypassTodayCheck, shh) {
			function log(text) {
				if (!shh) {
					Logging.warning(text);
				}
			}

			if (!planAccessCheck.isAdminSync) {
				return true;
			}

			if (!ticket) {
				return true;
			}
			var currentPromise =
				ticket.data.promisePeriodStack && ticket.data.promisePeriodPosition
					? ticket.data.promisePeriodStack[ticket.data.promisePeriodPosition]
					: undefined;
			if (!currentPromise) {
				return true;
			}

			if (ticket.data.plannedFinish !== currentPromise.plannedFinish) {
				log('can only remove black pins');
				return true;
			}
			if (currentPromise.actualFinish || currentPromise.varianceId) {
				return true;
			}
			if (!bypassTodayCheck && moment($scope.today.date.$value).isAfter(ticket.data.plannedFinish, 'day')) {
				log("can't remove historical pins");
				return true;
			}
		};

		$scope.removePin = function (ticket) {
			if ($scope.cantRemovePin(ticket)) {
				return true;
			}
			var pos = ticket.data.promisePeriodPosition;
			var previousPos;
			var hit = false;
			ticket.local.lastSnap.child('promisePeriodStack').forEach(function (snap) {
				if (!hit) {
					if (pos === snap.key) {
						hit = true;
						return;
					}
					previousPos = snap.key;
				}
			});

			//just delete it all
			if (!previousPos || previousPos === pos) {
				ticket.data.promisePeriodPosition = null;
				ticket.data.promisePeriodStack = null;
			}
			//move position
			else {
				ticket.data.promisePeriodStack[ticket.data.promisePeriodPosition] = null;
				ticket.data.promisePeriodPosition = previousPos;
			}
			delete ticket.local.promise;

			$scope.tickets.$save(ticket);
		};

		$scope.removePins = function (tickets) {
			if (!tickets) {
				return;
			}
			tickets.forEach(function (ticket) {
				if (ticket && ticket.data.promisePeriodStack) {
					ticket.data.promisePeriodStack = null;
					ticket.data.promisePeriodPosition = null;
					$scope.tickets.$save(ticket);

					delete ticket.local.promise;
				}
			});
		};

		$scope.removeSelectedPins = function () {
			var selecteds = this.planState.legacyActions.getOldTicketsFromNew(
				this.planState.tickets.selectedTicket$.getValue().list
			);
			return $scope.removePins(selecteds);
		};

		$scope.cantPin = function (ticket, shh) {
			function log(text) {
				if (!shh) {
					Logging.warning(text);
				}
			}
			if (!planAccessCheck.isAdminSync) {
				return true;
			}
			if (!ticket) {
				return true;
			}
			var plannedStart = ticket.data.plannedStart || ticket.data.lastPlannedStart;
			var plannedFinish = ticket.data.plannedFinish || ticket.data.lastPlannedFinish;
			if (!plannedFinish || !plannedStart) {
				return true;
			}
			if (ticket.data.actualFinish) {
				return true;
			}

			if (!$scope.activeLine || $scope.activeLine.$value === null) {
				return true;
			}
			if (ticket.data.left > $scope.activeLine.$value) {
				return true;
			}

			//validation pass
			//todo - also check for promises in the same position
			var currentPromise =
				ticket.data.promisePeriodStack && ticket.data.promisePeriodPosition
					? ticket.data.promisePeriodStack[ticket.data.promisePeriodPosition]
					: undefined;
			if (currentPromise) {
				if (currentPromise.actualFinish) {
					log('ticket ' + ticket.data.activityName + ' is completed');
					return true;
				}
				// if(!currentPromise.varianceId){ log('ticket '+ticket.data.activityName+' has not been addressed'); return true; }
				if (!currentPromise.varianceId) {
					return true;
				} //silently ignore

				var abort = false;
				Object.keys(ticket.data.promisePeriodStack).forEach(function (key) {
					var pp = ticket.data.promisePeriodStack[key];
					if (pp.plannedFinish === ticket.data.plannedFinish) {
						log('promise date on ticket ' + ticket.data.activityName + ' has already been promised');
						abort = true;
					}
				});
				if (abort) {
					return true;
				}
			}
		};
		$scope.cantPinIndividual = function (ticket, shh) {
			function log(text) {
				if (!shh) {
					Logging.warning(text);
				}
			}

			if ($scope.cantPin(ticket, shh)) {
				return true;
			}
			if (!$scope.today.date.$value) {
				return true;
			}
			if (moment($scope.today.date.$value).isAfter(ticket.data.plannedFinish, 'day')) {
				log('can not pin tickets set to finish in the past');
				return true;
			}
		};

		//pinData - an object with bonus data to add to the pin
		$scope.addPin = function (ticket, pinData) {
			if ($scope.cantPin(ticket)) {
				return true;
			}

			var plannedStart = ticket.data.plannedStart || ticket.data.lastPlannedStart;
			var plannedFinish = ticket.data.plannedFinish || ticket.data.lastPlannedFinish;

			var tickets = ticket.$parent;

			var stackRef = tickets.$ref().ref.child(ticket.$id).child('promisePeriodStack');
			var newStackId = stackRef.push();

			if (!ticket.data.promisePeriodStack) {
				ticket.data.promisePeriodStack = {};
			}
			ticket.data.promisePeriodStack[newStackId.key] = angular.extend(pinData || {}, {
				plannedFinish: plannedFinish,
				plannedStart: plannedStart,
				createdAt: fbdbTime.syncTime,
				createdBy: $scope.planState.authedProjectMemberSync.view.userId,
			});
			ticket.data.promisePeriodPosition = newStackId.key;

			//logger.log('ticket', ticket);
			//Logging.notice('ticket '+ticket.data.activityName+' was pinned');
			return tickets.$save(ticket);
		};

		$scope.addPins = function (tickets, pinData) {
			var p = [];
			tickets.forEach(function (ticket) {
				delete ticket.local.promise;
				p.push($scope.addPin(ticket, pinData));
			});
			return $q.all(p);
		};

		$scope.csPinSelected = function () {
			var selecteds = this.planState.legacyActions.getOldTicketsFromNew(
				this.planState.tickets.selectedTicket$.getValue().list
			);
			return $scope.addPins(selecteds, { pinType: 'csDidIt' });
		};

		$scope.focusPeriod = function () {
			if (!$scope.pp.currentPromise) {
				return;
			}
			var startX = $scope.activeColumnService.guessXFromDate($scope.pp.currentPromise.startDate);
			var endX = $scope.activeColumnService.guessXFromDate($scope.pp.currentPromise.endDate);

			var avg = (startX + endX) / 2;
			$scope.hud.setPositionExact(-avg * $scope.hud.z + window.innerWidth / 2, $scope.hud.y, $scope.hud.z);
		};

		firebaseList.push($scope.pp);

		//test code for sharing plan movement
		/*
		$scope.shared = {
			coords: fbConnection.fbObject('plans/'+ legacyState.projectId +'/'+ legacyState.planId+'/sharedCoords'),
			beMaster: function(){
				$scope.hud.observers.push(function(){
					$scope.shared.coords.x = $scope.hud.x;
					$scope.shared.coords.y = $scope.hud.y;
					$scope.shared.coords.z = $scope.hud.z;
					$scope.shared.coords.$save();
				});
			},
			beFollower: function(){
				$scope.shared.coords.$watch(function(){
					$scope.hud.setPositionExact($scope.shared.coords.x, $scope.shared.coords.y,$scope.shared.coords.z);
				});
			}

		};
		firebaseList.push($scope.shared.coords);
	*/

		//lod
		$scope.lod = {
			forceMax: function () {
				$scope.lod.watch.observe(
					this.setState(this.tiers[this.tiersIndex[this.tiersIndex.length - 1]]),
					true
				);
			},
			//debug function that sets to lod to max and disables it's update function, effectively disabling it
			// limitToMax: function(){
			// 	$scope.lod.watch.observe(this.setState(this.tiers[this.tiersIndex[this.tiersIndex.length-1]]), true);
			// 	var idx = $scope.hud.observers.indexOf(lodObsFn);
			// 	if(idx !== -1){
			// 		$scope.hud.observers.splice(idx,1);
			// 	}
			// },
			watch: subscriber.create(),
			lastTier: -1,
			tiers: {
				0: ['hide', 'hide-promise'],
				0.1: ['lod-text-only-on', 'hide-promise'],
				0.2: [],
			},
			stateList: ['hide', 'lod-text-only-on', 'hide-promise'],
			workingState: { hide: true, 'lod-text-only-on': false, 'hide-promise': true }, //default is nothing while it's loading
			setState: function (states) {
				for (var i = 0; i < this.stateList.length; i++) {
					this.workingState[this.stateList[i]] = false;
				}
				for (var i = 0; i < states.length; i++) {
					this.workingState[states[i]] = true;
				}
				return this.workingState;
			},
			update: function () {
				if (!$scope.hud.hasDoneInitialFit) {
					return;
				}
				//if jumped to tier, event
				for (var i = this.tiersIndex.length - 1; i > 0; i--) {
					if ($scope.hud.z > this.tiersIndex[i]) {
						break;
					}
				}
				if (i !== $scope.lod.lastTier) {
					//logger.log('this.tiers[this.tiersIndex[i]]', this.tiers[this.tiersIndex[i]]);
					$scope.lod.watch.observe(this.setState(this.tiers[this.tiersIndex[i]]));
					$scope.lod.lastTier = i;
				}
			},
		};
		$scope.lod.tiersIndex = Object.keys($scope.lod.tiers).sort();

		$scope.addSwimlane = function () {
			if (!$scope.planAccessCheck.isAdminSync || $scope.planAccessCheck.hasRestrictedAccessSync) {
				return;
			}
			const camera = this.planState.canvasConnector.camera;
			// TP-464: Special casing swimlane position validator check
			if (
				!$scope.datePositionValidator.isSwimlanePositionValid(camera.globalView.y, camera.globalView.height)
			) {
				Logging.warning(SWIMLANE_CANNOT_CREATE_TO_FAR);
				return;
			}

			this.planState.swimlaneDragService.includeTickets = false;
			this.planState.swimlanes.addItem({});
			analyticsEventsService.addedSwimlane();
		};

		$scope.setViewLabor = function (value: boolean) {
			// logger.log("CLICKED VIEW LABOR", value);
		};

		//--------------------------------
		// new ticket testing
		//--------------------------------
		const ticketsPath = fancyFirebaseGetters.getTicketPath(legacyState.projectId, legacyState.planId);
		$scope.tickets = firebaseTickets(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(ticketsPath, legacyState.projectId),
			{
				whoAmI: 'tickets',
			}
		);
		firebaseList.push($scope.tickets);

		$scope.tickets.$loaded(() => {
			window.perf.log('legacy tickets loaded', { previousEventName: 'planState - angular' });
			$scope.clearBlocker = true;
			// if($scope.activeLine && $scope.tickets){
			// 	$scope.scaleToFit();
			// }
			var lodObsFn = function () {
				$scope.lod.update.apply($scope.lod);
			};
			$scope.orderedExecution.add('updateLod', lodObsFn);

			$scope.orderedExecution.add(function () {
				if ($scope.lastTimelineGroupMenu) {
					$scope.lastTimelineGroupMenu.clear();
				}
			});

			//make this work outside loaded
			let ticketId: string = this.planState.route.snapshot.paramMap.get('ticketId');
			if (ticketId) {
				forkJoin([
					this.planState.tickets.list$.pipe(
						map((ticketsMap) => ticketsMap.get(ticketId)),
						filter((ticketNewData) => !!ticketNewData),
						take(1)
					),
					this.planState.stateChange$.pipe(
						// You might wonder why we don't use === StateEvents.CANVAS_LOADED.
						// The reason is that when we check if the state is CANVAS_LOADED, we also check if Angular and the plan controller are loaded.
						// By the time we reach this check, both are usually already loaded, so the calculation determines that the application is fully loaded.
						// As a result, the system almost never enters the CANVAS_LOADED state and instead skips directly to FULLY_LOADED.
						// Ideally, the state transitions would be sequential without skipping any states, but due to how the system works,
						// we check if the state is at least CANVAS_LOADED or higher.
						filter((state) => state >= StateEvents.CANVAS_LOADED),
						take(1)
					),
				]).subscribe(([ticketNewData]) => {
					if (ticketNewData) {
						$scope.hud.centerTicket($scope.tickets.$getRecord(ticketId), 1);
						this.planState.actions.changeTicketViewToEdit(ticketNewData);
					}
				});
			}
			$scope.procoreTimeout = null;
			// There isn't a reliable $loaded for constraints.  So, once tickets are loaded,
			// we'll assume constraints can't be too far behind, so we call the updateLinkedRFIs(..)
			// function with a timeout here (if this is a Procore linked project).
			if ($scope.project && $scope.project.procoreProjectId) {
				$scope.procoreTimeout = setTimeout(function () {
					$scope.procore.updateLinkedRFIs($scope.project.procoreProjectId, $scope.project.procoreCompanyId);
				}, 2000);
			}
		});

		function abortLoading(message?) {
			if (!message) {
				message = 'plan does not exist';
			}

			Logging.warning(message);
			$timeout(function () {
				Logging.notice('redirecting to dashboard shortly...');
				$timeout(function () {
					legacyRouting.go('projects');
				}, 3000);
			}, 3000);
		}

		//**************************************************************************
		// *  Initilization
		//**************************************************************************

		//stupid typescript...
		var tf: any = TicketFilter;
		tf.globals($scope);

		projectNotifier.setProjectAndPlan(legacyState.projectId, legacyState.planId);

		$scope.you = { userId: appState.userSync.$id };

		$scope.meshedUsers = firebaseMeshedUsersGlobal.getSync();

		$scope.meshedUsers.$loaded().then(function () {
			//logger.log('loadddddd');
			updateSplit(true);
			unwatchList.push($scope.meshedUsers.$watch(soakedUpdateSplit));
		});

		function updateSplit(first) {
			var oldNewLength = $scope.rallyMembers.newList.length;
			//logger.log('relist');
			$scope.rallyMembers.oldList.length = 0;
			$scope.rallyMembers.newList.length = 0;

			$scope.meshedUsers.forEach(function (mu) {
				if (!mu.data.isApi) {
					// Don't add API users:
					if (!mu.data.roleId) {
						$scope.rallyMembers.newList.push(mu);
					} else {
						$scope.rallyMembers.oldList.push(mu);
					}
				}
			});

			// logger.log('updateSplit', $scope.planAccessCheck.isAdminSync, first !== true, $scope.rallyMembers.newList.length, oldNewLength, $scope.selectedSidebar);
			if (
				$scope.planAccessCheck.isAdminSync &&
				first !== true &&
				$scope.rallyMembers.newList.length > oldNewLength
			) {
				// if($scope.selectedSidebar !== "rallyPoint" && $scope.selectedSidebar !== "rallyPointFull"){
				var text = 'A new user has joined ' + $scope.plan.name;
				if ($scope.accessCode && $scope.accessCode.$value !== null && $scope.accessCode._isValid) {
					text += ' using Rally Point.';
				}
				Logging.notice(text);
				// }
			}
		}
		var soakedUpdateSplit = soak(updateSplit);
		$scope.updateSplit = updateSplit;

		//$scope.projectMembers = firebaseProjectMembers(legacyState.projectId);
		$scope.roles = firebaseRoles(legacyState.projectId, $scope.roleColors);

		const locationsPath = fancyFirebaseGetters.getLocationsPath(legacyState.projectId);
		$scope.locations = firebaseLocations(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(locationsPath, legacyState.projectId)
				.orderByChild('name'),
			$scope.locationColors
		);

		const projectExceptionDaysPath = fancyFirebaseGetters.getProjectExceptionDaysPath(legacyState.projectId);
		$scope.projectWorkdays = firebaseProjectWorkdays(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(projectPath, legacyState.projectId),
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
				projectExceptionDaysPath,
				legacyState.projectId
			)
		);

		$scope.priorities = new firebasePriorities(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectPath, legacyState.projectId)
				.child('priorities')
		);
		firebaseList.push($scope.roles.fb, $scope.locations, $scope.projectWorkdays, $scope.priorities);

		//get projectMember data,
		//at some point refactor into the projectMember service, allowing the ability to pull both one and all
		var pmPromise = firebaseIndices
			.projectMember(legacyState.projectId, appState.userSync?.$id)
			.then(function (projectMemberId) {
				if (projectMemberId.$value === null) {
					return null;
				} //forward the null

				const projectMemberPath = fancyFirebaseGetters.getProjectMembersPath(
					legacyState.projectId,
					projectMemberId.$value
				);
				const floatingTicketPath = fancyFirebaseGetters.getProjectMemberTicketsPath(
					legacyState.projectId,
					projectMemberId.$value
				);

				$scope.you.projectMember = fbConnection.fbObject(
					fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
						projectMemberPath,
						legacyState.projectId
					)
				);
				$scope.floatingTickets = firebaseTickets(
					fancyFirebaseReferencer
						.getLegacyRef<firebase.database.Reference>(floatingTicketPath, legacyState.projectId)
						.orderByChild('userOrder'),
					{
						readOnlyImunity: true,
						whoAmI: 'floatingTickets',
						planIdFn: function () {
							return legacyState.planId;
						},
					}
				);
				//override to ignore
				$scope.floatingTickets.isReadOnly = function () {
					return false;
				};
				function updateFloatingRegionApi() {
					$timeout(function () {
						if ($scope.floatingRegionApi && $scope.floatingRegionApi.update) {
							$scope.floatingRegionApi.update();
						}
					});
				}

				$scope.floatingTickets.$loaded().then(function () {
					$scope.floatingTickets.$watch(updateFloatingRegionApi);
					updateFloatingRegionApi();
				});

				firebaseList.push($scope.you.projectMember, $scope.floatingTickets);
				return $scope.you.projectMember.$loaded();
			});
		$scope.floatingRegionApi = {};

		const path = fancyFirebaseGetters.getSnapshotMetaPath(legacyState.projectId, legacyState.planId);
		const ref = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			path,
			legacyState.projectId
		);
		$scope.snapshots = firebaseSnapshots(ref);
		firebaseList.push($scope.snapshots.fb);

		//put this here for now...
		$scope.locationListApi = {};
		$scope.roleListApi = {};

		$scope.projectMembers = firebaseProjectMembers(legacyState.projectId);
		firebaseList.push($scope.projectMembers.fb);

		$scope.promiseLockInProgress = false;

		$scope.currentRole = {}; // RoleItem chosen at login

		$scope.showMilestoneEdit = false;

		// We need a global thing to keep track of the current
		// max z-index so that we can always have the last dragged
		// element on top
		$scope.maxZIndex = 1;

		// localItems is for data that should stay local to the client
		$scope.localItems = {};

		$scope.ticketOps = new TicketOps($scope, fbConnection);

		$scope.statusOptions = firebaseVariances(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectPath, legacyState.projectId)
				.child('variances')
				.orderByChild('varianceReason')
		);

		firebaseList.push($scope.statusOptions);

		$scope.removeProjectMember = function (pmId, userId) {
			return popup({ title: 'Are you sure you want to remove this user?' }).then(() => {
				// var pm = this.projectMemberList.fb.$getRecord(id);
				// logger.log('pm', pm, this.projectMemberList);
				return $scope.projectMembers.remove(legacyState.projectId, userId, pmId);
			});
		};

		$scope.planUrl = legacyState.projectId + '/' + legacyState.planId + '/';

		// ****************************
		// Firebase Connections
		// ****************************
		firebaseUsers.getUsers().then(function (users) {
			$scope.users = users;
			//firebaseList.push($scope.users);
		});

		const projectsPath = fancyFirebaseGetters.getProjectsPath(legacyState.projectId);
		const indicesUsersPath = fancyFirebaseGetters.getIndicesUsersPath(
			appState.userSync?.$id,
			legacyState.projectId
		);

		checkIfExists(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(projectsPath, legacyState.projectId)
		);
		checkIfExists(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
				indicesUsersPath,
				legacyState.projectId
			)
		);

		function checkIfExists(path) {
			fbConnection.loadObject(path).then(function (data) {
				if (data.$value === null) {
					data.$destroy();
					abortLoading();
				}
				watcherStore.push(
					data.$watch(function () {
						if (data.$value === null) {
							data.$destroy();
							abortLoading('your access to this plan has been lost');
						}
					})
				);
			});
		}

		//plans is "clean",
		//plansArray is a filtered version...
		const plansPath = fancyFirebaseGetters.getPlanPath(legacyState.projectId);

		/**
		 * Moving the plansList$ to $scope is the first step in migrating the legacy code to the new data structure.
		 * Currently, this plansList$ is used by:
		 * - ProjectViewDirective
		 *
		 * If you find any other instances where this sync list is used, please add them to the list above.
		 */
		$scope.plansList$ = this.planState.plansApi.data.list$;

		$scope.plans = fbConnection.fbArray(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(plansPath, legacyState.projectId)
		);
		$scope.plans.$watch(function () {
			//$scope.plansArray = $filter('filter')($scope.plans, {deletedAt: '!', id: '!constraints'});
			$scope.plansArray = $filter('filter')($scope.plans, { deletedAt: '!' });
		});

		//break this out into it's own firebase object to increase update speed
		$scope.growActiveSpace = fbConnection.fbObject(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(plansPath, legacyState.projectId)
				.child('growActiveSpace')
		);
		$scope.growPullSpace = fbConnection.fbObject(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(plansPath, legacyState.projectId)
				.child('growPullSpace')
		);

		$scope.plan = fbConnection.fbObject(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
		);
		firebaseList.push($scope.plans, $scope.growActiveSpace, $scope.growPullSpace, $scope.plan);

		$scope.accessCode = null;
		$scope.plan.$watch(function () {
			if (!$scope.accessCode || $scope.plan.accessCode !== $scope.accessCode.$id) {
				if ($scope.accessCode && $scope.accessCode.$destroy) {
					$scope.accessCode.$destroy();
				}
				$scope.accessCode = rallyPointService.getAccessCode($scope.plan.accessCode);
			}
		});

		$scope.plan.$loaded().then(function () {
			if ($scope.plan.$value === null) {
				abortLoading();
				return;
			} else if ($scope.plan.deletedAt) {
				abortLoading('this plan has been queued for deletion');
				return;
			}

			analyticsEventsService.viewedAPlan($scope.plan.planId, $scope.plan.name);

			var last;
			function updateCache() {
				last = {
					activeFinishX: $scope.plan.activeFinishX,
					fbdbTimeX: $scope.plan.fbdbTimeX,
					milestoneTargetX: $scope.plan.milestone ? $scope.plan.milestone.targetX : null,
					milestoneScheduleX: $scope.plan.milestone ? $scope.plan.milestone.scheduleX : null,
				};
			}
			updateCache();

			watcherStore.push(
				$scope.plan.$watch(function () {
					if ($scope.plan.$value === null) {
						abortLoading('your access to this plan has been lost');
					}

					if (
						last.activeFinishX !== $scope.plan.activeFinishX ||
						last.fbdbTimeX !== $scope.plan.fbdbTimeX ||
						($scope.plan.milestone && last.milestoneTargetX !== $scope.plan.milestone.targetX) ||
						($scope.plan.milestone && last.milestoneScheduleX !== $scope.plan.milestone.scheduleX)
					) {
						shiftApi.emit('update');
					}
					updateCache();
				})
			);

			//active column/ line stuff
			var width = $scope.plan.calDayWidthPixels;
			$scope.activeLine = fbConnection.fbObject(
				fancyFirebaseReferencer
					.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
					.child('activeLineX')
			);
			$scope.activeLineDate = fbConnection.fbObject(
				fancyFirebaseReferencer
					.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
					.child('activeLineDate')
			);

			function update() {
				activeColumnService.recalc($scope.activeLine.$value, $scope.activeLineDate.$value, width);
			}

			var p = [
				$scope.tickets.$loaded(),
				$scope.activeLine.$loaded(),
				$scope.plans.$loaded(),
				// Since plans are now loaded via the plansApi.data.list$, we need to wait for the Plan List to finish loading before proceeding.
				// An arbitrary 500ms delay is added to ensure the list is fully loaded, as loaded$() uses a heuristic to determine list completion.
				firstValueFrom($scope.planState.plansApi.data.loaded$().pipe(delay(500))),
			];
			$q.all(p).then(function () {
				// $scope._allLoaded determines whether the projectView should be displayed once all data has been loaded.
				$scope._allLoaded = true;
				if ($scope.activeLine && $scope.tickets) {
					if (!settingStorage.exists('projectView') || !settingStorage.get('projectView').autoPositionHud) {
						$scope.scaleToFit();
					}
				}
			});

			activeColumnService.init($scope.plan.activeLineX, $scope.plan.activeLineDate, width, $scope.hud);

			$scope.activeColumnService = activeColumnService;
			$scope.activeLine.$watch(update);
			$scope.activeLineDate.$watch(update);

			var activateColumnFlag = fbConnection.fbObject(
				fancyFirebaseReferencer
					.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
					.child('activatePullColumn')
			);
			activateColumnFlag.$watch(function () {
				if (activateColumnFlag.$value === null) {
					update();
				}
			});
			firebaseList.push($scope.activeLine, $scope.activeLineDate, activateColumnFlag);
		});

		$scope.planHasRestrictedAccess = (planId) => {
			const project = this.planState.project.syncObject;
			const planAccess = this.planState.plansApi.planAccessApi.data.syncList.get(planId);
			const authedProjectMember = this.planState.authedProjectMemberSync;
			return hasRestrictedAccess(project, planAccess, authedProjectMember);
		};

		$scope.lockPlan = () => {
			if ($scope.planAccessCheck.isAdminSync) {
				this.planState.plansApi.planAccessApi.lockPlan(legacyState.planId);
			}
		};
		$scope.activatePlan = () => {
			if ($scope.planAccessCheck.isAdminSync) {
				this.planState.plansApi.planAccessApi.activatePlan(legacyState.planId);
			}
		};

		//calculates a best-guess x position from a provided date, using the timeline (pull side),
		//a right of milestone fake position
		//then an active column check
		//returns null if it absolutely can't find anything
		$scope.getColumnPositionFromDate = function getColumnPositionFromDate(date) {
			if (!date) {
				return null;
			}
			var calced = null;
			//is pullColumn
			var pc = $scope.pullColumns.$getDateRecord(date);
			if (pc) {
				calced = pc.finishX;
			}
			//is right of milestone, fake a position
			else if ($scope.pullColumns.length && date > $scope.pullColumns[0].plannedFinish) {
				calced = $scope.pullColumns[0].finishX + 300;
			} else if (
				$scope.activeLineDate &&
				$scope.activeLine &&
				$scope.activeLineDate.$value &&
				$scope.activeLine.$value !== null &&
				date > $scope.activeLineDate.$value
			) {
				calced = $scope.activeLine.$value + 300;
			}
			//is active or off screen
			else {
				var activeX = activeColumnService.dateMap(date);
				if (activeX !== null && activeX !== undefined) {
					calced = activeX;
				}
			}
			return calced;
		};
		$scope.getDateFromPosition = function getDateFromPosition(leftX, rightX) {
			var date = null;
			if (!rightX) {
				rightX = leftX;
			}
			if (leftX > $scope.activeLine.$value) {
				var col = $scope.pullColumns.$getXRecord(rightX);
				if (col) {
					date = col.plannedFinish;
				}
			} else {
				var col = $scope.activeColumnService.getDateFromX(rightX);
				if (col) {
					date = col;
				}
			}
			//logger.log('leftX', leftX, col, date);
			return date;
		};

		const constraintsPath = fancyFirebaseGetters.getConstraintsPath(legacyState.projectId);
		const projectTicketsPath = fancyFirebaseGetters.getTicketPath(legacyState.projectId);
		const timelineTicketsPath = fancyFirebaseGetters.getTimelineTicketsPath(legacyState.projectId);
		//new constraints
		$scope.constraints2 = new firebaseConstraints2(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
				constraintsPath,
				legacyState.projectId
			),
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
				projectTicketsPath,
				legacyState.projectId
			)
		);

		$scope.timelineTickets = new firebaseTimelineTickets(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
				timelineTicketsPath,
				legacyState.projectId
			),
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
				projectTicketsPath,
				legacyState.projectId
			)
		);

		firebaseList.push($scope.constraints2, $scope.timelineTickets);

		$scope.gotoNewPlan = function (planId, ticketId) {
			if (!planId) {
				Logging.warning('plan not found');
				return;
			}
			legacyRouting.go('plan', legacyState.projectId, planId, { ticketId: ticketId });
		};

		$scope.goToPlanProjectView = function (planId) {
			if (!planId) {
				Logging.warning('plan not found');
				return;
			}
			legacyRouting.go('plan', legacyState.projectId, planId);
			analyticsEventsService.projectViewJumpToNewPlan();
		};

		$scope.toggleProjectView = function () {
			if (!$scope.projectViewApi.loaded) {
				Logging.warning(
					'Project Overview is disabled. Please try again in a few seconds or try refreshing the page.'
				);
				return;
			}
			// Fires up the renderer or stops the renderer, depending on the case.
			if ($scope.selectedSidebar !== 'projectView') {
				$scope.projectViewApi.stop();
			} else {
				$scope.projectViewApi.start();
			}
		};

		$scope.tickets.addListener('remove', function (list, data) {
			data.forEach(function (t) {
				if (t.data.type === 'constraint') {
					var c = $scope.constraints2.$getByTicketId(t.$id);
					if (c) {
						$scope.constraints2.$remove(c);
					}
				}

				if (t.data.type === 'milestone' || t.data.type === 'constraint') {
					var tc = $scope.timelineTickets.$getCompositeRecord(legacyState.planId, t.$id);
					if (tc) {
						$scope.timelineTickets.$remove(tc);
					}
				}
			});
		});
		$scope.tickets.addListener('duplicate', function (list, data) {
			logger.log('duplicate', data, list);
			data.forEach(function (t) {
				if (t.data.type === 'constraint') {
					var ref = $scope.constraints2.addIndex(legacyState.planId, t.$id);
					//$scope.timelineTickets.addIndex(ref.key, legacyState.planId, t.$id)
				}
			});
		});

		$scope.pullColumns = new firebasePullColumns(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
				.child('pullColumns')
		);
		firebaseList.push($scope.pullColumns);
		$scope.pullColumns.$loaded().then(function () {
			if ($scope.hud) {
				// Add Column lines and date labels
				$scope.timeline = new Timeline($scope, {
					overlay: $('.zoomContent-overlay-timeline'),
					underlay: $('.zoomContent-underlay-ui'), //refactor to something better, init timeline in hud perhaps?
				});
				$scope.timeline.recalc();

				//maintains $scope.lag
				var updateDateDiff = function () {
					var days = $scope.milestone.daysBehind || 0;
					$scope.lag = {
						days: Math.abs(days),
						behind: days > 0,
					};
				};

				//milestone
				fbConnection
					.loadObject(
						fancyFirebaseReferencer
							.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
							.child('milestone')
					)
					.then(function (data) {
						$scope.milestone = data;
						watcherStore.push(data.$watch(updateDateDiff));
						updateDateDiff();
						firebaseList.push($scope.milestone);
					});

				watcherStore.push(
					$scope.pullColumns.$watch(function () {
						$scope.timeline.recalc();
					})
				);

				//fbdbTimeX
				$scope.today = {
					line: fbConnection.fbObject(
						fancyFirebaseReferencer
							.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
							.child('fbdbTimeX')
					),
					date: fbConnection.fbObject(
						fancyFirebaseReferencer
							.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
							.child('fbdbTime')
					),
				};
				firebaseList.push($scope.today.line, $scope.today.date);
				$scope.today.line.$loaded().then(function () {
					watcherStore.push(
						$scope.today.line.$watch(function () {
							$scope.hud.update();
						})
					);
				});

				$scope.milestoneX = fbConnection.fbObject(
					fancyFirebaseReferencer
						.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
						.child('milestone/targetX')
				);
				$scope.scheduleX = fbConnection.fbObject(
					fancyFirebaseReferencer
						.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
						.child('milestone/scheduleX')
				);
				$scope.milestoneName = fbConnection.fbObject(
					fancyFirebaseReferencer
						.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
						.child('milestone/activityName')
				);

				firebaseList.push($scope.milestoneX, $scope.scheduleX, $scope.milestoneName);

				$scope.ticketConstants = {};
				[
					'calDayWidthPixels',
					'calMinWidthPixels',
					'calTicketHeightPixels',
					'pullTicketHeightPixels',
					'pullTicketWidthPixels',
				].forEach(function (val) {
					$scope.ticketConstants[val] = fbConnection.fbObject(
						fancyFirebaseReferencer
							.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
							.child(val)
					);
					firebaseList.push($scope.ticketConstants[val]);
				});
			} else {
				var msg = "Hud hasn't been inited by this point, numerous things will break. Report this.";
				logger.warn(msg);
				Logging.warning(msg);
			}
		});

		//legacy patch to use the planState version of project data
		$scope.project = {};
		const projSubscription = this.planState.project.object$
			.pipe(
				filter((proj) => !!proj),
				map((proj) => {
					//patch on a $id to make it look more like the old version
					return { ...proj, $id: proj.projectId };
				})
			)
			.subscribe((project) => {
				$scope.project = project;
				$scope.projectSettings.update();
			});

		const firebaseListUnsubCompat = { $destroy: () => projSubscription.unsubscribe() };
		firebaseList.push(firebaseListUnsubCompat);

		//roles
		$q.all([$scope.roles.fb.$loaded(), pmPromise]).then(function () {
			var pm = $scope.you.projectMember;
			if (!pm) {
				Logging.warning('no projectMember associated with user');
				return;
			}

			$scope.currentRole = $scope.roles.fb.$getRecord(pm.roleId);
			if (!$scope.currentRole) {
				$scope.currentRole = { data: {} };
				if ($scope.you.projectMember.accessLevel === 'Admin') {
					Logging.warning("You don't have a role, assign one on the dashboard under Project Members");
				} else {
					//Logging.warning('You are not currently assigned a role, please ask your admin to correct.');
				}
				analyticsEventsService.missingRoleOnPlanLoad(
					$scope.you.projectMember.$id,
					$scope.you.projectMember.accessLevel === 'Admin',
					legacyState.planId
				);
			}
			$scope.workDaysLocal = {
				displayWorkDays: $scope.currentRole.data.workShiftsByDow ? $scope.currentRole : 'project',
			};

			var pmWatch = $scope.you.projectMember.$watch(function (event) {
				logger.log(event);
				$scope.currentRole = $scope.roles.fb.$getRecord(pm.roleId);
				$scope.currentRole = $scope.currentRole ? $scope.currentRole : { data: {} };

				var adminPart = $scope.you.projectMember.accessLevel === 'Admin' ? 'and admin access' : '';
				var roleName = checkNested($scope.currentRole, 'data', 'roleName')
					? $scope.currentRole.data.roleName
					: 'n/a';
				//Logging.notice('Your account has been modified, you now have the role: "' + roleName + '" ' + adminPart);
				//$scope.roleSelector.toggle();
			});
			watcherStore.push(pmWatch);
		});

		$scope.deleteActive = function () {
			if ($scope.plan.activeLineX === undefined || $scope.plan.activeLineX === null) {
				return;
			}
			var ticketCount = 0;

			$scope.tickets.forEach(function (t) {
				if (t.data.left < $scope.plan.activeLineX) {
					ticketCount++;
				}
			});
			if (ticketCount > 0) {
				//Logging.warning("Can't remove active space, you have active tickets");
				return;
			}

			const deleteObj = {
				activeLineX: null,
				activeLineData: null,
				promisePeriodPosition: null,
				promisePeriodStack: null,
			};
			return $scope.plan
				.$ref()
				.update(deleteObj)
				.then(() => {
					$scope.orderedExecution.execute();
				});
		};

		$scope.createActive = function () {
			if (!$scope.activeLineDate || $scope.activeLineDate.$value !== null) {
				return;
			}

			var lefty = $scope.tickets.min();
			if (lefty && lefty.data.plannedStart) {
				var date = moment(lefty.data.plannedStart).subtract(1, 'days').format('YYYY-MM-DD');
				//var date = "2017-06-12";
				var left = lefty.data.left - 100;
			} else {
				var date =
					$scope.today.date !== null
						? moment($scope.today.date).add(2, 'weeks').format('YYYY-MM-DD')
						: moment().add(2, 'weeks').format('YYYY-MM-DD');
				var left = lefty ? lefty.data.left - 100 : 1000;
			}

			var obj = {
				activeLineX: left,
				activeLineDate: date,
			};
			//logger.log('obj', obj);
			$scope.plan
				.$ref()
				.update(obj)
				.then(function () {
					$scope.orderedExecution.execute();
				});
		};

		//**************************************************************************
		//*  UI
		//**************************************************************************
		$scope.resetSamplePlan = function () {
			fbdbRequests
				.requestPlanOverwrite(
					{
						projectId: 'sampleProjectV2',
						planId: 'samplePlanV2',
					},
					{
						projectId: legacyState.projectId,
						planId: legacyState.planId,
					},
					appState.userSync?.$id
				)
				.then(
					function (data) {
						$scope.hud.scaleToFit();
						logger.log('done', data);
					},
					function (err) {
						logger.log('err', err);
					}
				);
		};

		$scope.activateColumn = function activateColumn(idx) {
			var index = idx;

			//idx not set
			if (!index && index !== 0) {
				index = $scope.pullColumns.length - 1;
			}
			if (!$scope.pullColumns || !$scope.pullColumns[index]) {
				return;
			}

			if (isNaN(index)) {
				logger.log('index is not a number');
				return;
			}

			fbConnection
				.set(
					fancyFirebaseReferencer
						.getLegacyRef<firebase.database.Reference>(planPath, legacyState.projectId)
						.child('activatePullColumn'),
					index
				)
				.then(function () {
					// analyticsEventsService.activatedAColumn($scope.plan.$id, $scope.plan.name, index);
				});
		};

		$scope.roleSelector = {
			enable: false,
			toggle: function () {
				this.enable = !this.enable;
			},
		};

		$scope.dispatchZoomToFitSegment = function () {
			// Dispatch segment event, but only if the 'Zoom to Fit' button is clicked
			analyticsEventsService.zoomToFit($scope.plan.$id, $scope.plan.name, $scope.project.projectName);
		};

		$scope.dispatchZoomToPrintSegment = function () {
			// Dispatch segment event, but only if the 'Zoom to Fit' button is clicked
			analyticsEventsService.zoomToPrint($scope.plan.$id, $scope.plan.name, $scope.project.projectName);
		};

		//zooms the plan to fit everything
		$scope.scaleToFit = function () {
			return $scope.hud.scaleToFit();
		};

		$scope.clickNub = function ($event) {
			if ($scope.activeLine.$value === null) {
				$scope.createActive();
			} else {
				$scope.deleteActive();
			}

			$event.stopPropagation();
			$event.preventDefault();
			return false;
		};

		$scope.alignRows = function (tickets = $scope.tickets.selection.selectedItems) {
			var a = new Alignment($scope);
			a.sort(tickets, 'top');
			a.align();
			a.verify();
			a.save($scope.tickets.massSave);
		};

		//**************************************************************************
		//*  Navigation
		//**************************************************************************

		$scope.dashboard = function (e) {
			// Return to the dashboard page passing back the same state as
			// when the user left there originally.
			activeColumnService.reset();
			analyticsEventsService.exitPlan(legacyState.planId);

			legacyRouting.go('projects', legacyState.projectId);
		};

		//*********************************************************************
		//*  Other
		//*********************************************************************

		$scope.invitedUsers = firebaseInvitedUsers(legacyState.projectId);

		$scope.openAngularRoleDialog = () => {
			this.projectDialogLauncher.openRoleDialog({
				rolesApi: this.planState.rolesApi,
				roleColorApi: this.planState.roleColorsApi,
				isAdmin: this.planState.authedProjectMemberSync?.view.accessLevel === 'Admin',
			});
		};

		$scope.openAngularLocationDialog = () => {
			this.projectDialogLauncher.openLocationDialog({
				locationsApi: this.planState.locationsApi,
				locationColorApi: this.planState.locationColorsApi,
				isAdmin: this.planState.authedProjectMemberSync?.view.accessLevel === 'Admin',
			});
		};

		$scope.openAngularProjectMemberDialog = () => {
			this.projectDialogLauncher.openMemberDialog({
				projectId: this.planState.projectId,
				membersApi: this.planState.projectMembersApi,
				invitedApi: this.planState.invitedUsersApi,
				rolesApi: this.planState.rolesApi,
				ownerUserId: this.planState.project?.syncObject.ownerUserId,
				isAdmin: this.planState.authedProjectMemberSync?.view.accessLevel === 'Admin',
			});
		};

		//types = ['constraints', 'roles', 'locations', 'projectMembers, 'snapshots', 'reports', 'planSearchAndFiltering, null];
		$scope.selectedSidebar = null;
		$scope.toggleSidebar = function (originalType) {
			const type = originalType;

			const angularDialogsEnabled = true;

			//completely bypass the rest of this "selected sidebar" stuff
			if (angularDialogsEnabled && originalType === 'roles') {
				$scope.openAngularRoleDialog();
				return;
			}
			if (angularDialogsEnabled && originalType === 'locations') {
				$scope.openAngularLocationDialog();
				return;
			}
			if (angularDialogsEnabled && originalType === 'projectMembers') {
				$scope.openAngularProjectMemberDialog();
				return;
			}

			// logger.log('toggling');
			// Poke project presence everytime report panel is opened
			if (originalType === 'reports') {
				pokeProjectPresence(legacyState.projectId, appState.userSync?.$id, fancyDatabase);
			}

			// Refresh procore data if this is a procore project, but only on opening
			// the constraints panel
			if (type === 'constraints' && type !== $scope.selectedSidebar && $scope.project.procoreProjectId) {
				$scope.procore.updateLinkedRFIs($scope.project.procoreProjectId, $scope.project.procoreCompanyId);
			}

			if (type === $scope.selectedSidebar) {
				$scope.selectedSidebar = null;
				//logger.log($scope.selectedSidebar, "Closed");
			} else {
				$scope.selectedSidebar = type;
				if (type === 'promisePeriods') {
					analyticsEventsService.promiseSettingsOpened();
				}
				// if (type === "projectView"){$scope.toggleProjectView();}
				if (type === 'rallyPoint') {
					analyticsEventsService.rallyPointOpened();
				}
				if (type === 'snapshots') {
					analyticsEventsService.snapshotLogOpened(legacyState.planId);
				}
				if (type === 'projectView') {
					$scope.toggleProjectView();
				}
			}

			if (type === 'planSearchAndFiltering' && $scope.selectedSidebar != null) {
				planTicketsFilterRemoteControlService.openPlanTicketsFilter();
				planPrintPanelRemoteControlService.closePrintPlantSettingsControl();
				planPrintService.closeView();
			} else {
				planTicketsFilterRemoteControlService.closePlanTicketsFilter();
				planPrintPanelRemoteControlService.closePrintPlantSettingsControl();
				planPrintService.closeView();
			}

			this.planState.legacyActions.markConstraintSidePanelDirty();
		};
		$scope.setSidebar = function (type) {
			$scope.selectedSidebar = type;
		};
		$scope.closeSidebar = $scope.toggleSidebar.bind($scope, $scope.selectedSidebar);

		$scope.resetLocalStorage = () => {
			localStorage.clear();
			//give it a moment... just because cache is sketchy
			setTimeout(() => {
				window.location.reload();
			}, 200);
		};

		//proof of concept ticket/plan collection
		var depMap = {
			ticket: function (planId, ticketId) {
				var obj = $scope.tickets.$getRecord(ticketId);
				if (obj) {
					var pos = {
						left: $scope.dragCache[ticketId]
							? $scope.dragCache[ticketId].left || obj.data.left
							: obj.data.left,
						top: $scope.dragCache[ticketId] ? $scope.dragCache[ticketId].top || obj.data.top : obj.data.top,
						width: $scope.dragCache[ticketId]
							? $scope.dragCache[ticketId].width || obj.data.width
							: obj.data.width,
						height: $scope.dragCache[ticketId]
							? $scope.dragCache[ticketId].height || obj.data.height
							: obj.data.height,
					};
					return {
						ticketId: obj.$id,
						planId: legacyState.planId,
						obj: obj,
						top: pos.top * $scope.hud.z + $scope.hud.y,
						left: pos.left * $scope.hud.z + $scope.hud.x,
						width: pos.width * $scope.hud.z,
						height: pos.height * $scope.hud.z,
					};
				}
				return null;
			},
			constraint: function (planId, ticketId) {
				var obj = $scope.timelineTickets.$getCompositeRecord(planId, ticketId);
				if (obj) {
					//logger.log('obj', obj);
					if (obj.data.type === 'constraint' && !obj.data.plannedFinish) {
						return null;
					} //shits broken
					return {
						ticketId: obj._debugIdxData.ticketId,
						planId: obj._debugIdxData.planId,
						obj: obj,
						top: 77,
						left: obj.local.timelineData ? obj.local.timelineData.x - 20 : 0,
						width: 20,
						height: 20,
					};
				}
				return null;
			},
		};

		//should theoretically be callable by the dep iteration without any particular problems (perf if anything)
		$scope.getTicketAndPlan = function (planId, ticketId) {
			for (var key in depMap) {
				var match = depMap[key].apply(undefined, arguments);
				if (match) {
					return match;
				}
			}
			return null;
		};
		$scope.item = function (id) {
			var item = $scope.tickets.$getRecord(id);
			if (item) {
				item.type = 'ticket';
				return item;
			}
			return {};
		};
		//when constraints gets updated to the new system
		//this function needs to be nuked and every reference needs to be updated
		$scope.itemUnwrapped = function (id) {
			var item = $scope.item(id);
			if (item.type === 'ticket') {
				item.data.type = 'ticket';
				return item.data;
			} else {
				return item;
			}
		};

		//just calls $scope.item, angular confuses item when refered from the html
		$scope.itemFromArrays = function (id) {
			return $scope.item(id);
		};

		$scope.readOnlyReaction = function (from) {
			if (from) {
				Logging.warning('Cannot move ' + from + ' when plan is locked');
			} else {
				Logging.warning('Plan is locked. You can only status your tickets. Only admins can unlock plans.');
			}
		};

		$scope.timelineTicketReadOnlyReaction = (isSourcePlan = false) => {
			const restrictedMessage: string = isSourcePlan
				? strings.TIMELINE_TICKETS_PLAN_TICKET_RESTRICTED
				: strings.TIMELINE_TICKETS_CURRENT_PLAN_RESTRICTED;
			$scope.bannerMessageHandler.displayWarningMessage(
				`${restrictedMessage}. ${strings.TIMELINE_TICKETS_ONLY_ADMINS_CAN_MODIFY_ACCESS}.`
			);
		};

		window.screenSpace = function (val) {
			if ((!val && val !== 0) || isNaN(val)) {
				return 'bad argument';
			}
			if (!$scope.hud || !$scope.hud.z || !$scope.hud.x) {
				return "hud isn't defined";
			}
			logger.log('z', $scope.hud.z, 'x', $scope.hud.x);
			return val * $scope.hud.z + $scope.hud.x;
		};

		//*********************************************************************
		//*  Milestones
		//*********************************************************************

		$scope.showMilestoneEdit = false;

		$scope.toggleMilestone = function () {
			$scope.showMilestoneEdit = !$scope.showMilestoneEdit;
			$scope.preEditMilestone = angular.extend({}, $scope.milestone);
			if (!$scope.milestone.targetDate) {
				$scope.milestoneHasNoDate = true;
			}
		};

		// Save what's in the text boxes out to firebase and dismiss the dialog
		$scope.saveAndDismissProjectMilestone = function () {
			if ($scope.activeLine.$value !== null && !$scope.milestone.targetDate) {
				Logging.warning('invalid milestone, reverting to previous');
				$scope.milestone.targetDate = $scope.preEditMilestone.targetDate;
				$scope.showMilestoneEdit = false;
			}
			$scope.milestone.$save();
			$scope.timeline.recalc();

			$scope.showMilestoneEdit = false;

			if ($scope.milestoneHasNoDate && $scope.milestone.targetDate) {
				//added
				// analyticsEventsService.milestoneSet($scope.milestone.activityName, $scope.milestone.targetDate, legacyState.planId);
			} else {
				analyticsEventsService.milestoneChanged(
					$scope.milestone.activityName,
					$scope.milestone.targetDate,
					legacyState.planId
				);
			}
			delete $scope.milestoneHasNoDate;
		};

		// delete what's in the text boxes and persist empty strings to firebase
		$scope.deleteProjectMilestone = function () {
			popup().then(
				function () {
					$scope.milestone.activityName = '';
					$scope.milestone.targetDate = '';
					$scope.milestone.$save();
					$scope.showMilestoneEdit = false;
					analyticsEventsService.milestoneChanged(
						$scope.milestone.activityName,
						$scope.milestone.targetDate,
						legacyState.planId
					);
					delete $scope.milestoneHasNoDate;
				},
				function () {
					//$scope.showMilestoneEdit = false;
				}
			);
		};

		//*********************************************************************
		//*  Testing
		//*********************************************************************

		window.shiftActiveLineRight = function (x) {
			if (isNaN(x)) {
				return 'not a number';
			}
			if (!$scope.activeLine || !$scope.activeLineDate) {
				return 'no line';
			}
			var d = moment($scope.activeLineDate.$value).subtract(x, 'days').format('YYYY-MM-DD');
			var l = $scope.activeLine.$value - $scope.ticketConstants.calDayWidthPixels.$value * x;

			// logger.log('d', d, 'l', l);
			const pathOfPlan = fancyFirebaseGetters.getPlanPath(legacyState.projectId, legacyState.planId);
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(pathOfPlan, legacyState.projectId)
				.update({
					activeLineDate: d,
					activeLineX: Math.floor(l),
				});
		};

		$scope.popupDebug = function () {
			popup(
				{ template: '<div ticket-import="ticketOps.bulkAdd(list)" map="processCsv(list)"></div>' },
				$scope
			);
		};

		$scope.processCsv = function (data) {
			var newList = [];
			var failed;

			var usefulVals = {
				'Activity Name': 'activityName',
				activityName: 'activityName',
				'Actual Finish': 'actualFinish',
				'Crew Size': 'crewSize',
				'Duration Days': 'durationDays',
				durationDays: 'durationDays',
				left: 'left',
				'Promise Date': 'datePromised',
				Role: 'tempRoleName',
				Location: 'tempLocationName',
				Status: 'status',
				'Ticket X Position': 'left',
				'Ticket Y Position': 'top',
				'Last Planned Start': 'lastPlannedStart',
				'Last Planned Finish': 'lastPlannedFinish',
				top: 'top',
				'Variance Reason': 'varianceReason',
				Type: 'type',
				'Date Requested': 'dateRequested',
				'Creator Project Member Id': 'creatorId',
				'Responsible Project Member Id': 'responsibleProjectMemberId',
				'Assigned Project Member Id': 'assignedProjectMemberId',
				'Priority Id': 'priorityId',
			};

			var supportedDates = ['actualFinish', 'lastPlannedStart', 'lastPlannedFinish', 'dateRequested'];

			data.forEach(function (line, lineNo) {
				var obj = {};
				var count = 0;
				for (var key in usefulVals) {
					var a = line[key];
					if (a !== undefined && a !== null && a !== '') {
						var actualKey = usefulVals[key];
						//special processing
						if (actualKey === 'type') {
							if (typeof a === 'string') {
								a = a.toLowerCase();
								//must be a valid type
								if (a !== 'task' && a !== 'constraint' && a !== 'milestone') {
									failed = 'type of "' + a + '" is invalid. Line no: ' + (lineNo + 1);
								}
							}
						}
						if (actualKey === 'activityName') {
							a = String(a);
						}

						obj[actualKey] = a;
						count++;
					}
				}
				if (count) {
					newList.push(obj);
				}
			});

			newList.forEach((ticket, lineNo) => {
				if (
					ticket.durationDays &&
					ticket.durationDays !== 1 &&
					ticket.type &&
					(ticket.type === 'milestone' || ticket.type === 'constraint')
				) {
					failed = "milestones and constraints can't have a non-one duration. Line no: " + (lineNo + 1);
				}
				supportedDates.forEach((dateKey) => {
					var d = ticket[dateKey];
					var customRange = {
						minDate: moment().subtract(10, 'years'),
						maxDate: moment().add(10, 'years'),
					};
					// logger.log('date', dateKey, d, DatePositionValidator.isValidDate(d));
					if (d && !DatePositionValidator.isValidDate(d, customRange)) {
						failed = dateKey + ' is invalid "' + d + '" for Line no: ' + (lineNo + 1);
					}
				});
			});

			Logging.notice('Importing ' + newList.length + ' tickets');
			logger.log('newList', newList);

			if (failed) {
				Logging.warning(failed);
			}
			logger.log('failed', failed);
			return failed ? false : newList;
		};

		$scope.showFloatingMilestoneCal = function () {
			if (!$scope.planAccessCheck.isAdminSync || $scope.planAccessCheck.hasRestrictedAccessSync) {
				return;
			}
			logger.log('tapmilestone');

			var displayName = $scope.milestone.activityName ? $scope.milestone.activityName : '';

			var htmlString =
				'<p class="header">Milestone name and date</p><input value="' +
				displayName +
				'" class="txt-input" type="text" placeholder="Milestone Name">';

			var lastMilestone = angular.extend({}, $scope.milestone);

			function saveMilestone(date, html) {
				logger.log(date, html);
				if (!date) {
					$scope.milestone.targetDate = null;
					$scope.milestone.activityName = null;
					$scope.milestone.$save();
					return;
				}
				var newName = html.closest('.txt-input').val();

				if (newName !== $scope.milestone.activityName || date !== $scope.milestone.targetDate) {
					$scope.milestone.targetDate = date ? date : null;
					$scope.milestone.activityName = newName !== '' ? newName : null;
					logger.log('save milestone', newName, date);
					$scope.milestone.$save().then(function () {
						// $scope.plan will always be created and point to the correct path, so use it to construct a new reference
						// for the targetFinishDate.
						let childRef = $scope.plan.$ref().child('targetFinishDate');
						childRef.set(date);
						// MOC-2659 TRY AGAIN2
						// $scope.plan["targetFinishDate"] = date;
						// $scope.plan.$save();
					});
				}
				if (lastMilestone.$value === null) {
					// analyticsEventsService.milestoneSet($scope.milestone.activityName, $scope.milestone.targetDate, legacyState.planId);
				} else {
					analyticsEventsService.milestoneChanged(
						$scope.milestone.activityName,
						$scope.milestone.targetDate,
						legacyState.planId
					);
				}
			}

			$scope.milestoneLineCalendarShutdown = $scope.floatingCalendar(
				$scope.milestone.targetDate,
				htmlString,
				function (date, html) {
					DatePositionValidator.verifyDate(
						date,
						{
							title: 'Are you sure you want to set the milestone date to: ' + date + '?',
							description: 'Your milestone date is outside of the expected date range for your plan.',
						},
						saveMilestone,
						null,
						date,
						html
					);
				}
			);
		};

		$scope.saveActiveLine = function (date) {
			if (!date) {
				return;
			}

			analyticsEventsService.activeLineSetViaCalendar(legacyState.planId, $scope.plan.name, date);

			if ($scope.activeLine && $scope.activeLine.$value !== null) {
				if ($scope.plan.activeFinishDate && moment(date).isBefore($scope.plan.activeFinishDate, 'days')) {
					Logging.warning('Active line cannot be moved to a date before scheduled tickets');
					return;
				}
				if (
					!$scope.pp.isInitialPeriod &&
					$scope.pp.currentPromise &&
					moment(date).isBefore($scope.pp.currentPromise.endDate, 'days')
				) {
					Logging.warning('Active line cannot be moved to a date before scheduled tickets');
					return;
				}
				var days = moment($scope.activeLineDate.$value, 'YYYY-MM-DD').diff(date, 'days');
				var x = -days * $scope.activeColumnService.columnWidth();

				var extra = { initiatedBy: 'systemOnActiveLineChange' };
				return $scope.snapshots
					.takeSnapshot(legacyState.projectId, legacyState.planId, appState.userSync?.$id, extra)
					.then(function () {
						$scope.plan.$ref().update({ growActiveSpace: x });
					});
			} else {
				if ($scope.tickets.length) {
					var x = $scope.tickets.min().data.left - 24;
				} else {
					var x = 0;
				}

				var extra = { initiatedBy: 'systemOnActiveLineChange' };
				return $scope.snapshots
					.takeSnapshot(legacyState.projectId, legacyState.planId, appState.userSync?.$id, extra)
					.then(function () {
						$scope.plan.$ref().update({ activeLineX: x, activeLineDate: date });
					});
			}
		};

		$scope.showFloatingActiveCal = function (defaultDate?: moment.MomentInput) {
			if (!$scope.planAccessCheck.isAdminSync || $scope.planAccessCheck.hasRestrictedAccessSync) {
				return;
			}

			const dateToDisplayInitially = !!defaultDate ? defaultDate : $scope.activeLineDate.$value;

			$scope.activeLineCalendarShutdown = $scope.floatingCalendar(
				dateToDisplayInitially,
				'<p class="header">Select active line date</p>',
				{ noDelete: true },
				function (date) {
					DatePositionValidator.verifyDate(
						date,
						{
							title: 'Are you sure you want to set the active line date to: ' + date + '?',
							description:
								'Your defined active line date is outside of the expected date range for your plan.',
						},
						$scope.saveActiveLine,
						null,
						date
					);
				}
			);
		};
		$scope.trustAsHtml = function (value) {
			return $sce.trustAsHtml(value);
		};

		$scope.specNag = function () {
			Logging.notice('spec me...');
			setTimeout(function () {
				Logging.notice('speeeeeeecc...');
			}, 1000);
			setTimeout(function () {
				Logging.notice('...');
			}, 2000);
			setTimeout(function () {
				Logging.notice('SPEC!');
			}, 5000);
		};

		$scope.parseRoleForSumo = function (role) {
			if (!role) {
				return '';
			}
			//logger.log('role', role);
			return "<div class='" + role.$id + " woo'></div>";
		};
		$scope.parseLocationForSumo = function (location) {
			if (!location) {
				return '';
			}
			return (
				"<div style='background: " +
				$scope.locationColors.getColor(location.data.locationColor) +
				"'; class='woo'></div>"
			);
		};
		//Should add Admin Checks to this
		$scope.accessChecker = {
			notLocked: function (displayWarning, displayActionName) {
				if ($scope.planAccessCheck.hasRestrictedAccessSync) {
					if (displayWarning)
						Logging.warning(
							strings.UNABLE_TO_PERFORM_ACTION_BECAUSE_PLAN_ACCESS(
								displayActionName,
								$scope.planAccessCheck.accessLevelSync
							)
						);
					return false;
				} else {
					return true;
				}
			},
			notLockedIsAdmin: function (displayWarning, displayActionName) {
				if (!$scope.planAccessCheck.isAdminSync) {
					if (displayWarning)
						Logging.warning(strings.UNABLE_TO_PERFORM_ACTION_BECAUSE_NOT_ADMIN(displayActionName));
					return false;
				} else if ($scope.planAccessCheck.hasRestrictedAccessSync) {
					if (displayWarning)
						Logging.warning(
							strings.UNABLE_TO_PERFORM_ACTION_BECAUSE_PLAN_ACCESS(
								displayActionName,
								$scope.planAccessCheck.accessLevelSync
							)
						);
					return false;
				} else {
					return true;
				}
			},
			canActivateColumn: function (displayWarning, displayActionName) {
				if (!$scope.planAccessCheck.isAdminSync) {
					if (displayWarning)
						Logging.warning(strings.UNABLE_TO_PERFORM_ACTION_BECAUSE_NOT_ADMIN(displayActionName));
					return false;
				} else if ($scope.planAccessCheck.hasRestrictedAccessSync) {
					if (displayWarning)
						Logging.warning(
							strings.UNABLE_TO_PERFORM_ACTION_BECAUSE_PLAN_ACCESS(
								displayActionName,
								$scope.planAccessCheck.accessLevelSync
							)
						);
					return false;
				} else if (
					!$scope.milestoneX ||
					$scope.milestoneX.$value === undefined ||
					$scope.milestoneX.$value === null
				) {
					if (displayWarning)
						Logging.warning(
							strings.UNABLE_TO_PERFORM_ACTION_BECAUSE_NEED_TO_CREATE_MILESTONE(displayActionName)
						);
					return false;
				}

				return true;
			},
			canPromisePeriod: function (displayWarning, displayActionName) {
				if (!$scope.planAccessCheck.isAdminSync) {
					if (displayWarning)
						Logging.warning(strings.UNABLE_TO_PERFORM_ACTION_BECAUSE_NOT_ADMIN(displayActionName));
					return false;
				} else if ($scope.planAccessCheck.hasRestrictedAccessSync) {
					if (displayWarning)
						Logging.warning(
							strings.UNABLE_TO_PERFORM_ACTION_BECAUSE_PLAN_ACCESS(
								displayActionName,
								$scope.planAccessCheck.accessLevelSync
							)
						);
					return false;
				}
				if (
					!$scope.milestoneX ||
					$scope.milestoneX.$value === undefined ||
					$scope.milestoneX.$value === null
				) {
					if (displayWarning)
						Logging.warning(
							strings.UNABLE_TO_PERFORM_ACTION_BECAUSE_NEED_TO_CREATE_MILESTONE(displayActionName)
						);
					return false;
				} else if (!$scope.activeLine || $scope.activeLine.$value == null) {
					if (displayWarning)
						Logging.warning(
							strings.UNABLE_TO_PERFORM_ACTION_BECAUSE_NEED_TO_ACTIVATE_YOUR_PULL_COLUMN(displayActionName)
						);
					return false;
				}

				return true;
			},
			generic: function (msg, truthyThing) {
				if (truthyThing) {
					return true;
				}
				Logging.warning(msg);
				return false;
			},
		};

		$scope.promiseOutOfBoundsPopup = function () {
			popup({
				title: 'Some larger text',
				descriptionHtml:
					'A description indicating that the promise period is in the planning section instead<br>\
				 of the active section and that the active line will need to be advanced to see it.<br>\
				 Perhaps we could even display the date on which it ends.',
				template: require('html-loader?root=src/!tpl/popups/simple.html'),
			});
		};
		//Procore...
		$scope.procore = {
			// Find the tickets in the plan that are linked to ProcoreRFIs
			// and freshen up the status and requested date based on the
			// status and due date in Procore.
			updateLinkedRFIs: function (procoreProjectId, procoreCompanyId = 'none') {
				// Iterate constraints, get the procore linked tickets, and set up
				// a list to ship off to Procore that has RFI ID (procore) and the
				// corresponding ticket and contraint IDs (touchplan)
				const procoreLinkedTickets: {
					[key: string]: {
						ticketId: string;
						constraintId: string;
					};
				} = {};
				$scope.constraints2.forEach((constraint) => {
					if (constraint.data?.hasOwnProperty('procoreRfiId')) {
						procoreLinkedTickets[constraint.data.procoreRfiId] = {
							ticketId: constraint.data.id,
							constraintId: constraint.$id,
						};
					}
				});

				procoreService
					.listRFIs(procoreProjectId, procoreCompanyId, { filterRfiIds: Object.keys(procoreLinkedTickets) })
					.pipe(last())
					.subscribe(
						(RFIs) => {
							// Success case with fresh data out of Procore.  Update tickets
							RFIs.forEach((RFI) => {
								// MOC-3065 - Null check to fix timing issue where you receive a response from server right when you navigate away from page
								if (!$scope.tickets) {
									return;
								}
								const ticketRecord = $scope.tickets.$getRecord(procoreLinkedTickets[RFI.id].ticketId);
								if (ticketRecord === null) {
									return;
								}
								ticketRecord.data.dateRequested = RFI.due_date;
								ticketRecord.$parent.$save(ticketRecord);
								// if rfi.status is closed, update the touchplan ticket to closed.
								if (RFI.status === ProcoreRFIStatus.closed) {
									if (!ticketRecord.data.actualFinish) {
										// Use miniSave so that the UI and Procore close things the same way.
										$scope.constraints2.miniSave(procoreLinkedTickets[RFI.id].constraintId, {
											complete: true,
											procore: true,
											actualFinish: moment(RFI.time_resolved).format('YYYY-MM-DD'),
										});
									}
								}
							});
						},
						(errorResponse) => {
							if (errorResponse?.data) {
								Logging.warning(errorResponse.data.message);
							} else if (errorResponse) {
								Logging.warning(errorResponse);
							}
						}
					);
			},
			trackViewInProcore: function () {
				analyticsEventsService.procoreViewRfiInProcore($scope.project.$id, $scope.plan.$id);
			},
		};

		//ticket copy prototype
		function ticketCopy(tickets) {
			if (!tickets) {
				Logging.warning('nothing to copy');
				return;
			}
			//but does it leak?
			window.copiedTickets = tickets.map(function (t) {
				return t.data;
			});
		}
		function ticketPaste(tickets) {
			if (!tickets) {
				Logging.warning('nothing has been copied');
				return;
			}
			return $scope.ticketOps.bulkAdd(tickets);
		}

		$scope.ticketCopy = function () {
			var selecteds = this.planState.legacyActions.getOldTicketsFromNew(
				this.planState.tickets.selectedTicket$.getValue().list
			);
			return ticketCopy(selecteds);
		};
		$scope.ticketPaste = function () {
			return ticketPaste(window.copiedTickets);
		};

		//bad practice, but more stable. At least when considering our absurd architecture...
		// This moves hud creation out of planGestureManager and into the plan_controller.
		// Which means that the Hud will now be initialized before any child directives of the ticketspace,
		// so it should be no longer necessary to ensure it exists before use.
		function makeHud() {
			var element = $('#zoomContent-container');
			return new Hud(
				$(element), //container
				$('.zoomContent', element), //surface
				$('.zoomContent-overlay', element), //overlay
				$('.zoomContent-underlay-dependencies', element), //underlay
				$scope
			);
		}

		function dumpServicesOntoScope() {
			$scope.intercomHandler = intercomHandler;
			$scope.multipleRolesUtils = multipleRolesUtils;
			$scope.datePositionValidator = DatePositionValidator;
			$scope.$q = $q;
			$scope.Logging = Logging;
			$scope._ = _;
			$scope.activeColumnService = activeColumnService;
			$scope.analyticsEventsService = analyticsEventsService;
			$scope.dateJar = dateJar;
			$scope.developerMode = false; //developerMode;
			$scope.fbConnection = fbConnection;
			$scope.firebaseRoles = firebaseRoles;
			$scope.floatingCalendar = floatingCalendar;
			$scope.focusManager = focusManager;
			$scope.isNaN = isNaN;
			$scope.log = console.log;
			$scope.popup = popup;
			$scope.userPreferenceStateService = userPreferenceStateService;
			$scope.projectViewApi = ProjectViewApi;
			$scope.randomColor = randomColor;
			$scope.shiftApi = shiftApi;
			$scope.legacyRouting = legacyRouting;
			$scope.featureFlagManager = featureFlagManager;
			$scope.matDialog = matDialog;
			window.MCScope = $scope; // For debugging.
			$scope.systemColors = SystemColors; // Used to dynamically generate text colors.
			$scope.ticketEditService = ticketEditService;
			$scope.validatedPlanActions = validatedPlanActions;
			$scope.widePullTicketSub = featureFlagManager
				.getFlag$(DynamicFlagTypes.WIDE_PULL_TICKET)
				.subscribe((flag) => ($scope.widePullTicketEnabled = flag));

			//silly-stuff
			$scope.changeClass = sillyStuff.changeClass;
			$scope.teamUp = sillyStuff.teamUp;

			$scope.planAccessCheck = planAccessCheck;
			$scope.bannerMessageHandler = bannerMessageHandler;
		}

		$scope.setTextSize = function (option: string, value: number) {
			$scope.userPreferenceStateService.canvasTextOptions = option;
			$scope.userPreferenceStateService.canvasTextScale = value;
			if (window.TouchPlanCanvas) {
				window.TouchPlanCanvas.run(true);
			}
		};

		featureFlagManager
			.getFlag$(DynamicFlagTypes.PLAN_ACTIONS_HOVER_OVER)
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((flag) => ($scope.isTicketHoverEnabled = flag));

		featureFlagManager
			.getFlag$(DynamicFlagTypes.CUSTOM_FIELDS_MATERIAL_GROUP_ENABLED)
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((flag) => ($scope.isCustomFieldsMaterialGroupEnabled = flag));

		featureFlagManager
			.getFlag$(DynamicFlagTypes.TICKET_DEPENDENCY_LINKAGE_TYPE_AND_LAG)
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((flag) => ($scope.isTicketDependencyLinkageEnabled = flag));

		featureFlagManager
			.getFlag$(DynamicFlagTypes.MODERN_TIMELINE_ENABLED)
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((flag) => ($scope.enableUseModernTimeline = flag));

		overviewProjectService.openOverview$.pipe(takeUntil(this.shutdownSubject)).subscribe(() => {
			$scope.toggleSidebar('projectView');
		});

		$scope.isQuantityTrackingEnabled = false;
		let observable;
		if (this.planState.customFieldsApi) {
			observable = this.planState.customFieldsApi.getDefinitions().data.list$.pipe(
				map((list) => {
					let result: boolean;
					list.forEach((value) => {
						if (value.view.label === strings.QUANTITY_TYPE) {
							result = true;
						}
					});
					return result;
				})
			);
		} else {
			observable = of(false);
		}
		observable.subscribe((value) => ($scope.isQuantityTrackingEnabled = value));

		$scope.swimlanesDropdownData$ = reportsSwimlaneDataService.swimlanesDropdownData$;
		featureFlagManager
			.getFlag$(DynamicFlagTypes.SWIMLANES_REPORT_ENABLED)
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((isEnabled) => {
				if (isEnabled) {
					reportsSwimlaneDataService.loadSwimlaneDropdownData(this.planState.projectId);
				} else {
					reportsSwimlaneDataService.loadSwimlaneDropdownData(null);
				}
			});

		combineLatest([
			planTicketsFilterRemoteControlService.openPlanTicketsFilter$,
			planPrintPanelRemoteControlService.enablePlanPrintSettings$,
		])
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe(([isTicketsFilterOpen, isPrintSettingsOpen]) => {
				const isAnyOpen = isTicketsFilterOpen || isPrintSettingsOpen;

				if (isAnyOpen) {
					$scope.setSidebar('planSearchAndFiltering');
				} else if ($scope.selectedSidebar === 'planSearchAndFiltering') {
					$scope.selectedSidebar = null;
				}
			});
	}

	public $onInit() {
		let unsub = this.planState.stateChange$.subscribe((stateEvent: StateEvents) => {
			if (stateEvent > 0 && stateEvent < 100 && stateEvent !== StateEvents.SCOPE_SOUPED) {
				this.planState.assignScopeSoup(this.$scope);
				this.$scope.planState = this.planState;
			}
			if (stateEvent === StateEvents.BOOMED) {
				unsub.unsubscribe();
			}
		});

		this.doStuff(
			this.$filter,
			this.$http,
			this.$q,
			this.$rootScope,
			this.$sce,
			this.$scope,
			this.$timeout,
			this.DatePositionValidator,
			this.OrderedExecutor,
			this.ProjectViewApi,
			this.SystemColors,
			this.activeColumnService,
			this.appState,
			this.developerMode,
			this.fbConnection,
			this.fbdbRequests,
			this.firebaseConstraints2,
			this.firebaseCounter,
			this.firebaseIndex,
			this.firebaseIndices,
			this.firebaseInvitedUsers,
			this.firebaseLocations,
			this.firebaseMeshedUsersGlobal,
			this.firebasePriorities,
			this.firebaseProjectColors,
			this.firebaseProjectMembers,
			this.firebaseProjectWorkdays,
			this.firebasePromisePeriod3,
			this.firebasePullColumns,
			this.firebaseRefUrl,
			this.firebaseRoles,
			this.firebaseSnapshots,
			this.firebaseTickets,
			this.firebaseTimelineTickets,
			this.firebaseUsers,
			this.firebaseVariances,
			this.floatingCalendar,
			this.focusManager,
			this.intercomHandler,
			this.multipleRolesUtils,
			this.popup,
			this.projectNotifier,
			this.validatedPlanActions,
			this.rallyPointService,
			this.settingStorage,
			this.shiftApi,
			this.subscriber,
			this.ticketEditService,
			this.watcherStore,
			this.legacyRouting,
			this.featureFlagManager,
			this.overviewProjectService,
			this.planTicketsFilterRemoteControlService,
			this.planPrintPanelRemoteControlService,
			this.planPrintService,
			this.matDialog,
			this.projectDialogLauncher,
			this.logger,
			this.procoreService,
			this.userPreferenceStateService,
			this.reportsSwimlaneDataService,
			this.planAccessCheck,
			this.bannerMessageHandler
		);
	}
}

angular
	.module('ticketspaceApp.controllers')
	.component('planControllerComponent', {
		bindings: {
			planState: '<',
			routeSnapshot: '<',
		},
		template: require('html-loader?root=src/!tpl/ticketspace.html'),
		controller: PlanController,
	})

	.controller('placeholderController', function () {
		//logger.log('placeholderController', arguments);
	});

//-------------------------------------------------
// Upgrade Boilerplate
//-------------------------------------------------

@Directive({
	selector: 'plan-controller-component',
})
export class PlanControllerUpgradeDirective extends UpgradeComponent {
	@Input() public planState;
	@Input() public routeSnapshot;
	constructor(elementRef: ElementRef, injector: Injector, private activatedRoute: ActivatedRoute) {
		super('planControllerComponent', elementRef, injector);
	}
}
